/**
 * One status vocabulary for the whole customer app (A00 discipline 7).
 *
 * Every state the product shows maps to ONE of five tones, so the same state is the same colour in the
 * Wallet, the Marketplace, Credit and the Merchant portal. A user learns the vocabulary once. And the
 * label is always rendered as text, so colour is never the only cue (the WCAG requirement, and what
 * keeps a status legible in a screenshot).
 *
 * Screens map their own domain words onto this vocabulary rather than inventing tones. Add a new status
 * here, not a new colour map in a page.
 */

type StatusTone = 'positive' | 'pending' | 'negative' | 'held' | 'neutral';

const TONE: Record<StatusTone, string> = {
  positive: 'bg-accent/15 text-accent', // done, verified, healthy
  pending: 'bg-warning/15 text-warning', // in progress, needs attention, not yet an error
  negative: 'bg-danger/10 text-danger', // rejected, frozen, failed
  held: 'bg-info/15 text-info', // locked or pledged, a neutral hold
  neutral: 'bg-surface-sunken text-ink-subtle', // unknown or informational
};

const STATUS: Record<string, StatusTone> = {
  verified: 'positive',
  screened: 'positive',
  clear: 'positive',
  active: 'positive',
  settled: 'positive',
  paid: 'positive',
  issued: 'positive',
  performing: 'positive',
  tokenized: 'positive',
  reconciled: 'positive',
  approved: 'positive',
  eligible: 'positive',
  complete: 'positive',
  yours: 'positive',
  resolved: 'positive',
  pending: 'pending',
  'under review': 'pending',
  watch: 'pending',
  due: 'pending',
  raising: 'pending',
  forming: 'pending',
  calculated: 'pending',
  assigned: 'pending',
  escalated: 'pending',
  investigating: 'pending',
  'in diligence': 'pending',
  'awaiting participant': 'pending',
  upcoming: 'pending',
  'in review': 'pending',
  requested: 'pending',
  live: 'positive',
  draft: 'neutral',
  rejected: 'negative',
  declined: 'negative',
  frozen: 'negative',
  hit: 'negative',
  failed: 'negative',
  withdrawn: 'negative',
  closed: 'negative',
  blocked: 'negative',
  locked: 'held',
  pledged: 'held',
  held: 'held',
};

/** A status pill in the shared vocabulary. Pass an explicit `label` when the display word differs. */
export function StatusPill({ status, label }: { status: string; label?: string }) {
  const key = status.toLowerCase().replace(/_/g, ' ');
  const tone = STATUS[key] ?? 'neutral';
  return (
    <span
      className={`inline-flex shrink-0 rounded-pill px-2 py-0.5 text-micro font-medium capitalize ${TONE[tone]}`}
    >
      {label ?? key}
    </span>
  );
}

const RISK: Record<string, { tone: StatusTone; label: string }> = {
  low: { tone: 'positive', label: 'Lower risk' },
  medium: { tone: 'pending', label: 'Medium risk' },
  high: { tone: 'negative', label: 'Higher risk' },
};

/** A risk-band pill (low / medium / high), on the same five tones. */
export function RiskPill({ risk, className }: { risk: string; className?: string }) {
  const r = RISK[risk] ?? { tone: 'neutral' as StatusTone, label: risk };
  return (
    <span
      className={`inline-flex shrink-0 rounded-pill px-2 py-0.5 text-micro font-medium ${TONE[r.tone]} ${className ?? ''}`}
    >
      {r.label}
    </span>
  );
}

/** The tone class for a status, for the rare spot that needs the class rather than the pill. */
export function statusTone(status: string): string {
  return TONE[STATUS[status.toLowerCase().replace(/_/g, ' ')] ?? 'neutral'];
}
